import { Check } from 'lucide-react';
import {
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { K8sFlavor } from '@/api/k8s';
import { ADDON_CATALOG, NFS_FLAVOR_WHITELIST, type AddonDef, type AddonField } from './cluster-draft';

const noteStyle: React.CSSProperties = { display: 'block', fontSize: 'var(--vn-font-size-xs, 11px)', color: 'var(--vn-fg-muted)', marginTop: 4 };

export function AddonsStep({
  addons,
  flavors = [],
  onChange,
}: {
  addons: Record<string, Record<string, unknown>>;
  flavors?: K8sFlavor[];
  onChange: (addons: Record<string, Record<string, unknown>>) => void;
}) {
  const nfsFlavorOptions = flavors
    .filter((f) => NFS_FLAVOR_WHITELIST.includes(f.name))
    .map((f) => ({ value: f.name, label: `${f.name}${f.cores ? ` — ${f.cores} vCPU, ${f.memory} GB` : ''}` }));
  const toggle = (def: AddonDef) => {
    const cur = addons[def.key];
    if (cur?.enabled) {
      onChange({ ...addons, [def.key]: { ...cur, enabled: false } });
    } else {
      // Seed defaults on enable.
      const seed: Record<string, unknown> = { enabled: true };
      for (const f of def.fields) if (f.default !== undefined) seed[f.key] = f.default;
      onChange({ ...addons, [def.key]: { ...cur, ...seed } });
    }
  };
  const setField = (key: string, fkey: string, value: unknown) =>
    onChange({ ...addons, [key]: { ...addons[key], [fkey]: value } });

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {ADDON_CATALOG.map((def) => {
        const cfg = addons[def.key] ?? {};
        const on = !!cfg.enabled;
        return (
          <div key={def.key} style={{ border: `1.5px solid ${on ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`, borderRadius: 'var(--vn-radius-lg)', padding: 'var(--vn-space-3)' }}>
            <button type="button" onClick={() => toggle(def)} style={{ width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--vn-fg-default)', font: 'inherit' }}>
              <span>
                <span style={{ display: 'block', fontWeight: 600 }}>{def.label}</span>
                <span style={{ display: 'block', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', marginTop: 2 }}>{def.description}</span>
              </span>
              <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', border: `1.5px solid ${on ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`, background: on ? 'var(--vn-accent-solid)' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {on && <Check size={14} style={{ color: 'var(--vn-fg-on-solid)' }} />}
              </span>
            </button>

            {on && def.fields.length > 0 && (
              <div style={{ marginTop: 'var(--vn-space-3)', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--vn-space-3)' }}>
                {def.fields.filter((f) => !f.showIf || f.showIf(cfg)).map((f) => (
                  <AddonFieldInput
                    key={f.key}
                    field={f}
                    value={cfg[f.key]}
                    nfsFlavorOptions={nfsFlavorOptions}
                    onChange={(v) => setField(def.key, f.key, v)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
      <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        Add-ons are installed automatically after the cluster is created. Some add-ons may incur additional resource usage.
      </p>
    </div>
  );
}

function AddonFieldInput({
  field,
  value,
  nfsFlavorOptions,
  onChange,
}: {
  field: AddonField;
  value: unknown;
  nfsFlavorOptions: { value: string; label: string }[];
  onChange: (v: unknown) => void;
}) {
  if (field.type === 'switch') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', alignSelf: 'end', paddingBottom: 8 }}>
        <Switch checked={!!value} onCheckedChange={onChange} /> {field.label}
      </label>
    );
  }
  if (field.type === 'select') {
    const options = field.dynamicOptions === 'nfsFlavors' ? nfsFlavorOptions : field.options ?? [];
    return (
      <Field label={field.label}>
        {(p) => (
          <>
            <Select value={String(value ?? '')} onValueChange={onChange}>
              <SelectTrigger {...p}><SelectValue placeholder={options.length ? undefined : 'No flavors available'} /></SelectTrigger>
              <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            {field.note && <span style={noteStyle}>{field.note}</span>}
          </>
        )}
      </Field>
    );
  }
  return (
    <Field label={field.label}>
      {(p) => (
        <>
          <Input
            {...p}
            type={field.type === 'number' ? 'number' : 'text'}
            value={value == null ? '' : String(value)}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
          />
          {field.note && <span style={noteStyle}>{field.note}</span>}
        </>
      )}
    </Field>
  );
}
